<template>
    <div class="w100 h100">
        <!-- 视频封面展示 -->
        <div class="w100 h100 video-cover" role="button" tabindex="0">
            <img :src="coverSrc" :alt="alt" class="cover-image" loading="lazy">
            <div class="play-icon" aria-label="播放视频" @click="openPreview">
                <i class="el-icon-caret-right" style="font-size: 25px;color: #fff;"></i>
            </div>
        </div>

        <!-- 预览模态框 -->
        <div v-if="showModal" class="preview-modal">
            <span class="close-btn" @click="closePreview">&times;</span>

            <!-- 上一视频按钮 -->
            <div v-if="internalVideoList.length > 1" class="nav-btn prev-video-btn" @click="prevVideo">
                <span class="arrow">&#x2039;</span>
            </div>

            <div class="video-container flex row center middle">
                <video v-if="!videoLoading" :src="currentVideoSrc" controls class="modal-video" preload="metadata">
                    您的浏览器不支持视频播放
                </video>
                <div v-else class="video-loading">
                    <div class="loading-spinner"></div>
                    <div class="loading-text">视频加载中...</div>
                </div>
            </div>

            <!-- 下一视频按钮 -->
            <div v-if="internalVideoList.length > 1" class="nav-btn next-video-btn" @click="nextVideo">
                <span class="arrow">&#x203A;</span>
            </div>

            <!-- 清晰度选择菜单 -->
            <div v-if="internalVideoQualities.length > 1" class="quality-selector">
                <el-dropdown @command="selectQuality" placement="bottom-start">
                    <div class="quality-dropdown">
                        <span class="current-quality">{{ currentQuality }}</span>
                        <i class="el-icon-arrow-down dropdown-arrow"></i>
                    </div>
                    <el-dropdown-menu slot="dropdown">
                        <el-dropdown-item v-for="(quality, index) in internalVideoQualities" :key="index"
                            :command="index" :class="{ 'is-active': currentQuality === quality.label }">
                            {{ quality.label }}
                        </el-dropdown-item>
                    </el-dropdown-menu>
                </el-dropdown>
            </div>

            <!-- 视频计数器 -->
            <div v-if="internalVideoList.length > 1" class="video-counter">
                {{ internalCurrentVideoIndex + 1 }} / {{ internalVideoList.length }}
            </div>
        </div>
    </div>
</template>

<script>
import { getProfileVideo } from '@/api/talkytimes/personalData'

export default {
    props: {
        coverSrc: {
            type: String,
            required: true
        },
        videoId: {
            type: Number,
            default: null
        },
        videoQualities: {
            type: Array,
            default: () => []
        },
        // 新增：视频列表相关props
        videoList: {
            type: Array,
            default: () => []
        },
        currentVideoIndex: {
            type: Number,
            default: 0
        },
        alt: {
            type: String,
            default: ''
        }
    },
    data() {
        return {
            showModal: false,
            currentVideoSrc: '',
            currentQualityIndex: 0,
            videoLoading: false,
            internalVideoQualities: [], // 视频清晰度列表
            currentQuality: '',
            // 新增：视频切换相关状态
            internalCurrentVideoIndex: this.currentVideoIndex,
            internalVideoList: this.videoList
        }
    },
    methods: {
        async openPreview() {
            if (this.showModal) return;

            this.showModal = true;
            document.body.style.overflow = 'hidden';
            this.videoLoading = true;

            try {
                // 如果传入了videoList，使用视频列表模式
                if (this.videoList && this.videoList.length > 0) {
                    this.internalVideoList = this.videoList;
                    this.internalCurrentVideoIndex = this.currentVideoIndex;
                    this.loadCurrentVideoFromList();
                } else if (this.videoQualities && this.videoQualities.length > 0) {
                    // 单个视频，使用传入的videoQualities
                    this.internalVideoQualities = this.videoQualities;
                    this.internalCurrentVideoIndex = 0;
                    this.loadCurrentVideo();
                } else if (this.videoId) {
                    // 根据videoId获取该视频的不同清晰度版本
                    const res = await getProfileVideo(this.videoId);
                    this.internalVideoQualities = res.data || [];
                    this.internalCurrentVideoIndex = 0;
                    this.loadCurrentVideo();
                } else {
                    console.error('videoPreview: 需要传入videoId、videoQualities或videoList');
                    this.internalVideoQualities = [];
                    this.internalCurrentVideoIndex = 0;
                }
            } catch (error) {
                console.error('获取视频失败:', error);
            } finally {
                this.videoLoading = false;
            }
        },
        closePreview() {
            this.showModal = false;
            document.body.style.overflow = '';
            this.videoLoading = false;
            // 清理视频资源
            const video = document.querySelector('.modal-video');
            if (video) {
                video.pause();
                video.currentTime = 0;
            }
        },
        loadCurrentVideo() {
            if (this.internalVideoQualities.length > 0) {
                const video = this.internalVideoQualities[this.currentQualityIndex];
                this.currentVideoSrc = video.src || '';
                this.currentQuality = video.label || '';
            }
        },
        loadCurrentVideoFromList() {
            if (this.internalVideoList.length > 0) {
                const currentVideo = this.internalVideoList[this.internalCurrentVideoIndex];

                // 如果当前视频有videoQualities属性，使用它
                if (currentVideo.videoQualities && currentVideo.videoQualities.length > 0) {
                    this.internalVideoQualities = currentVideo.videoQualities;
                    // 保持当前选择的清晰度索引
                    if (this.currentQualityIndex >= this.internalVideoQualities.length) {
                        this.currentQualityIndex = 0;
                    }
                    this.loadCurrentVideo();
                } else {
                    // 如果没有videoQualities，直接使用视频URL
                    this.currentVideoSrc = currentVideo.src || currentVideo.url || '';
                    this.currentQuality = currentVideo.label || '视频';
                }
            }
        },
        selectQuality(index) {
            if (this.currentQualityIndex === index) {
                return;
            }

            // 保存当前播放时间
            const video = document.querySelector('.modal-video');
            const currentTime = video ? video.currentTime : 0;

            this.currentQualityIndex = index;
            this.loadCurrentVideo();

            // 恢复播放时间
            if (video) {
                video.addEventListener('loadedmetadata', () => {
                    video.currentTime = currentTime;
                }, { once: true });
            }
        },
        prevVideo() {
            if (this.internalVideoList.length > 1) {
                this.internalCurrentVideoIndex = (this.internalCurrentVideoIndex - 1 + this.internalVideoList.length) % this.internalVideoList.length;
                this.loadCurrentVideoFromList();
            }
        },
        nextVideo() {
            if (this.internalVideoList.length > 1) {
                this.internalCurrentVideoIndex = (this.internalCurrentVideoIndex + 1) % this.internalVideoList.length;
                this.loadCurrentVideoFromList();
            }
        },
        handleKeydown(e) {
            if (this.showModal) {
                switch (e.key) {
                    case 'Escape':
                        this.closePreview();
                        break;
                    case 'ArrowLeft':
                        if (this.internalVideoList.length > 1) {
                            this.prevVideo();
                        }
                        break;
                    case 'ArrowRight':
                        if (this.internalVideoList.length > 1) {
                            this.nextVideo();
                        }
                        break;
                }
            }
        }
    },
    mounted() {
        document.addEventListener('keydown', this.handleKeydown);
    },
    beforeDestroy() {
        // 清理资源
        if (this.showModal) {
            this.closePreview();
        }
        document.removeEventListener('keydown', this.handleKeydown);
    }
}
</script>

<style scoped>
.video-cover {
    position: relative;
    cursor: pointer;
    border-radius: 8px;
    overflow: hidden;
    transition: all 0.3s;
}

.cover-image {
    width: 100%;
    height: 100%;
    object-fit: contain;
    border-radius: 8px;
}

.play-icon {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    color: #fff;
    background-color: rgba(0, 0, 0, 0.6);
    border-radius: 50%;
    padding: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s ease;
}

.video-cover:hover .play-icon {
    background-color: rgba(0, 0, 0, 0.8);
    transform: translate(-50%, -50%) scale(1.1);
}

.preview-modal {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.9);
    z-index: 9999;
    display: flex;
    justify-content: center;
    align-items: center;
}

.video-container {
    position: relative;
    width: 800px;
    /* 默认宽度 */
    height: 450px;
    /* 默认高度，16:9 比例 */
    max-width: 90vw;
    /* 最大宽度不超过视窗的90% */
    max-height: 80vh;
    /* 最大高度不超过视窗高度的80% */
    min-width: 320px;
    /* 最小宽度 */
    min-height: 180px;
    /* 最小高度 */
}

.modal-video {
    width: 100%;
    height: 100%;
    object-fit: contain;
    /* 改回 contain 以显示完整视频 */
    border-radius: 8px;
}

.video-loading {
    color: #fff;
    display: flex;
    flex-direction: column;
    align-items: center;
}

.loading-spinner {
    width: 40px;
    height: 40px;
    border: 4px solid rgba(255, 255, 255, 0.3);
    border-top: 4px solid #fff;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-bottom: 10px;
}

.loading-text {
    font-size: 14px;
}

@keyframes spin {
    0% {
        transform: rotate(0deg);
    }

    100% {
        transform: rotate(360deg);
    }
}

.close-btn {
    position: absolute;
    top: 20px;
    right: 30px;
    color: #fff;
    font-size: 40px !important;
    cursor: pointer;
    z-index: 10000;
}

.close-btn:hover {
    color: #ccc;
}

/* 清晰度选择器样式 */
.quality-selector {
    position: absolute;
    top: 30px;
    left: 30px;
    z-index: 10000;
}

.quality-dropdown {
    background-color: rgba(0, 0, 0, 0.7);
    color: #fff;
    padding: 8px 16px;
    border-radius: 4px;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 8px;
    transition: background-color 0.3s ease;
    border: 1px solid rgba(255, 255, 255, 0.3);
}

.quality-dropdown:hover {
    background-color: rgba(0, 0, 0, 0.8);
}

.current-quality {
    font-size: 14px;
    font-weight: 500;
}

.dropdown-arrow {
    font-size: 24px !important;
    transition: transform 0.3s ease;
}

/* Element UI dropdown 自定义样式 */
.quality-selector .el-dropdown-menu {
    background-color: rgba(0, 0, 0, 0.8);
    border: 1px solid rgba(255, 255, 255, 0.3);
}

.quality-selector .el-dropdown-menu .el-dropdown-menu__item {
    color: #fff;
    font-size: 14px;
}

.quality-selector .el-dropdown-menu .el-dropdown-menu__item:hover {
    background-color: rgba(255, 255, 255, 0.1);
    color: #fff;
}

.quality-selector .el-dropdown-menu .el-dropdown-menu__item.is-active {
    background-color: rgba(255, 255, 255, 0.2);
    font-weight: 500;
}

/* 导航按钮样式 */
.nav-btn {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    background-color: rgba(0, 0, 0, 0.6);
    border-radius: 50%;
    width: 50px;
    height: 50px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    z-index: 10000;
    color: #fff;
    font-size: 30px;
    transition: background-color 0.3s ease;
}

.nav-btn:hover {
    background-color: rgba(0, 0, 0, 0.8);
}

.prev-video-btn {
    left: 20px;
}

.next-video-btn {
    right: 20px;
}

.arrow {
    font-size: 24px !important;
}

/* 视频计数器样式 */
.video-counter {
    position: absolute;
    bottom: 20px;
    left: 50%;
    transform: translateX(-50%);
    color: #fff;
    font-size: 16px;
    font-weight: 500;
    background-color: rgba(0, 0, 0, 0.6);
    padding: 5px 10px;
    border-radius: 5px;
    z-index: 10000;
}
</style>